import React, { useEffect, useRef, useState } from 'react';
import IconSelector from '../IconSelector';
import { getMemberExpandKey } from '../../utils/dashboard/memberTreeHelpers';
import { getMemberPresenceStatus } from '../../utils/dashboard/memberMarkers';
import { getMemberListDisplayName } from '../../utils/dashboard/commanderMemberNoteHelpers';
import { PRESENCE_STATUS } from '../../utils/dashboard/constants';
import { missionIdMatchesMember } from '../../utils/dashboard/deviceIdentity';
import MemberCollapsedCard, {
  FreqStatusBadge,
  TrackSwitch,
  formatCollapsedSeenText
} from '../MemberCollapsedCard';
import { VoiceIconButton } from '../MissionMemberChatWindow';

function formatMemberSeen(lastSeen, offline = false) {
  return formatCollapsedSeenText(lastSeen, { offline });
}


function findMissionTag(missions, member) {
  const list = Array.isArray(missions) ? missions : [];
  for (const m of list) {
    const ids = m?.memberIds || [];
    if (ids.some((id) => missionIdMatchesMember(id, member))) {
      return m.name || '任务';
    }
  }
  return null;
}

function formatFullSeen(lastSeen) {
  if (!lastSeen) return null;
  const d = new Date(lastSeen);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `更新于 ${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const actionBtn = {
  height: 28,
  padding: '0 10px',
  fontSize: 12,
  borderRadius: 6,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
  border: '1px solid #d0d7de',
  background: '#fff',
  color: '#333'
};

const actionBtnPrimary = {
  ...actionBtn,
  background: '#1976d2',
  borderColor: '#1976d2',
  color: '#fff',
  fontWeight: 500
};

const dropMenu = {
  position: 'absolute',
  bottom: '100%',
  left: 0,
  marginBottom: 4,
  minWidth: 132,
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
  padding: 4,
  zIndex: 30
};

const dropItem = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '7px 10px',
  fontSize: 12,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: 4,
  color: '#333'
};

function signalLabel(gsm) {
  if (gsm == null || gsm === '') return { text: '未知', color: '#9ca3af', bars: 0 };
  const raw = String(gsm);
  const n = Number(gsm);
  if (/良好|强|优|good|excellent/i.test(raw)) return { text: '良好', color: '#16a34a', bars: 4 };
  if (/一般|中|fair|mid/i.test(raw)) return { text: '一般', color: '#ca8a04', bars: 3 };
  if (/弱|差|poor|weak/i.test(raw)) return { text: '较弱', color: '#ea580c', bars: 2 };
  if (Number.isFinite(n)) {
    if (n >= 3 || n >= 70) return { text: '良好', color: '#16a34a', bars: 4 };
    if (n >= 2 || n >= 40) return { text: '一般', color: '#ca8a04', bars: 3 };
    return { text: '较弱', color: '#ea580c', bars: 2 };
  }
  return { text: raw, color: '#374151', bars: 3 };
}

function SignalBars({ bars = 0, color = '#16a34a' }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 1, height: 12, marginRight: 4 }}>
      {[1, 2, 3, 4].map((i) => (
        <span
          key={i}
          style={{
            width: 3,
            height: 3 + i * 2,
            borderRadius: 1,
            background: i <= bars ? color : '#e5e7eb'
          }}
        />
      ))}
    </span>
  );
}

function BatteryIcon({ pct }) {
  const n = Number(pct);
  const ok = Number.isFinite(n);
  const fill = !ok ? '#9ca3af' : (n > 50 ? '#16a34a' : (n > 20 ? '#ca8a04' : '#dc2626'));
  const w = ok ? Math.max(1, Math.min(10, Math.round((Math.max(0, Math.min(100, n)) / 100) * 10))) : 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <svg width="18" height="10" viewBox="0 0 18 10" aria-hidden="true">
        <rect x="0.5" y="1.5" width="14" height="7" rx="1.5" stroke={fill} fill="none" strokeWidth="1.2" />
        <rect x="15.2" y="3.2" width="2" height="3.6" rx="0.6" fill={fill} />
        <rect x="2" y="3" width={w} height="4" rx="0.5" fill={fill} />
      </svg>
      <span style={{ fontWeight: 700, color: '#111827' }}>{ok ? `${n}%` : '未知'}</span>
    </span>
  );
}

function MemberDetail({
  member,
  presenceStatus,
  onFlyTo,
  onToggleTrack,
  onStartRealtimeTrack,
  onStopRealtimeTrack,
  isRealtimeDrawing = false,
  hasDeviceTrack = false,
  onCopyCoords,
  onDownloadTrack,
  onFetchTrack,
  onIconChange,
  onOpenLiveStream,
  isStreaming = false,
  isHiddenOnMap = false,
  onToggleMapHidden,
  commandFeatures = {},
  onSendCommand = null,
  onOpenCommandMenu = null
}) {
  const [trackOpen, setTrackOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [iconEdit, setIconEdit] = useState(false);
  const boxRef = useRef(null);
  const deviceId = member.device_unique_id || member.name;
  const online = presenceStatus === PRESENCE_STATUS.ONLINE || presenceStatus === 'online';
  const presenceText = online
    ? '在线'
    : (presenceStatus === PRESENCE_STATUS.TEMP_OFFLINE || presenceStatus === 'temp_offline' ? '暂时离开' : '离线');

  useEffect(() => {
    if (!trackOpen && !moreOpen) return undefined;
    const onDown = (e) => {
      if (boxRef.current?.contains?.(e.target)) return;
      setTrackOpen(false);
      setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [trackOpen, moreOpen]);

  const fmtNum = (v, unit = '', digits = null) => {
    if (v == null || v === '') return '未知';
    const n = Number(v);
    if (!Number.isFinite(n)) return '未知';
    const text = digits != null ? n.toFixed(digits) : String(n);
    return `${text}${unit}`;
  };
  const canFetch = commandFeatures.fetchTrack !== false;
  const canView = commandFeatures.viewTrack !== false;
  const canDownload = commandFeatures.downloadTrack !== false;
  const canRealtime = commandFeatures.realtimeDraw !== false;
  const sig = signalLabel(member.gsm);

  const cell = (label, valueNode) => (
    <div style={{ minWidth: 0, display: 'flex', gap: 6, alignItems: 'baseline', lineHeight: 1.55 }}>
      <span style={{ color: '#9ca3af', flexShrink: 0, minWidth: 28 }}>{label}</span>
      <span style={{ color: '#111827', fontWeight: 600, minWidth: 0, wordBreak: 'break-all' }}>{valueNode}</span>
    </div>
  );

  const copyUid = () => {
    const uid = member.device_unique_id || member.name || '';
    if (!uid) return;
    if (onCopyCoords) onCopyCoords(String(uid));
    else if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(String(uid));
  };

  return (
    <div
      ref={boxRef}
      style={{
        marginTop: 0,
        padding: '10px 12px',
        background: '#fff',
        fontSize: 12,
        borderTop: '1px solid #e5e7eb',
        borderRadius: '0 0 8px 8px'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* 顶：状态 | 电量 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10,
        paddingBottom: 8,
        borderBottom: '1px solid #f3f4f6'
      }}
      >
        <div>
          <span style={{ color: '#9ca3af' }}>状态 </span>
          <span style={{ fontWeight: 700, color: online ? '#16a34a' : '#6b7280' }}>{presenceText}</span>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: '#9ca3af' }}>电量</span>
          <BatteryIcon pct={member.battery_level} />
        </div>
      </div>

      {/* 两列栅格 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '4px 16px',
        marginBottom: 10
      }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {cell('经度', member.last_longitude != null ? Number(member.last_longitude).toFixed(6) : '未知')}
          {cell('纬度', member.last_latitude != null ? Number(member.last_latitude).toFixed(6) : '未知')}
          {cell('海拔', member.last_altitude != null ? `${member.last_altitude} m` : '未知')}
          {cell('精度', fmtNum(member.last_accuracy, ' m'))}
          {cell('速度', fmtNum(member.last_speed, ' m/s', 1))}
          {cell('方向', fmtNum(member.last_bearing, '°', 1))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {cell('信号', (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <SignalBars bars={sig.bars} color={sig.color} />
              <span style={{ color: sig.color }}>{sig.text}</span>
            </span>
          ))}
          {cell('组织', member.org_name || '—')}
          {cell('小组', member.group_name || '—')}
        </div>
      </div>

      {/* 设备标识 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginBottom: 12,
        fontSize: 11,
        color: '#6b7280',
        minWidth: 0
      }}
      >
        <span style={{ flexShrink: 0 }}>设备标识</span>
        <span style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          color: '#374151',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0
        }}
        title={deviceId}
        >
          uid:{deviceId}
        </span>
        <button
          type="button"
          title="复制设备标识"
          onClick={(e) => { e.stopPropagation(); copyUid(); }}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#2563eb',
            cursor: 'pointer',
            padding: 0,
            fontSize: 12,
            flexShrink: 0
          }}
        >
          {'\u2398'}
        </button>
      </div>

      {/* 底操作栏 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        borderTop: '1px solid #f3f4f6',
        paddingTop: 10
      }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 'auto' }}>
          <b style={{ color: '#6b7280', fontWeight: 600 }}>图标</b>
          {iconEdit ? (
            <IconSelector
              value={member.icon || undefined}
              placeholder="默认图标"
              size="small"
              showPreview
              deviceId={deviceId}
              style={{ minWidth: 140, fontSize: 11 }}
              onChange={(icon) => {
                onIconChange?.(member, icon);
                setIconEdit(false);
              }}
              onIconChange={(icon) => {
                onIconChange?.(member, icon);
                setIconEdit(false);
              }}
            />
          ) : (
            <>
              <span
                title={member.icon || '默认'}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: '#d1d5db',
                  display: 'inline-block',
                  flexShrink: 0
                }}
              />
              <button
                type="button"
                onClick={() => setIconEdit(true)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#2563eb',
                  cursor: 'pointer',
                  fontSize: 12,
                  padding: 0,
                  fontWeight: 600
                }}
              >
                编辑
              </button>
            </>
          )}
        </div>

        <button
          type="button"
          style={actionBtn}
          onClick={(e) => {
            e.stopPropagation();
            if (onSendCommand) onSendCommand(member);
            else if (onOpenCommandMenu) onOpenCommandMenu(member);
            else openCommandFallback(member);
          }}
          title="发指令"
        >
          {'\u2708'} 发指令
        </button>
        <button
          type="button"
          style={actionBtn}
          onClick={(e) => {
            e.stopPropagation();
            if (member.last_longitude != null && member.last_latitude != null) {
              onCopyCoords?.(`${Number(member.last_longitude).toFixed(6)},${Number(member.last_latitude).toFixed(6)}`);
            } else {
              alert('该队员暂无有效坐标');
            }
          }}
          title="复制坐标"
        >
          {'\u2398'} 复制坐标
        </button>

        <div style={{ position: 'relative' }}>
          <button
            type="button"
            style={trackOpen ? actionBtnPrimary : actionBtn}
            onClick={(e) => {
              e.stopPropagation();
              setMoreOpen(false);
              setTrackOpen((v) => !v);
            }}
            title="轨迹操作"
          >
            轨迹 {'\u25BE'}
          </button>
          {trackOpen && (
            <div style={dropMenu} role="menu">
              <div style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6' }}>
                <TrackSwitch
                  on={!!isRealtimeDrawing}
                  onToggle={(next) => {
                    if (next) onStartRealtimeTrack?.(member);
                    else onStopRealtimeTrack?.(member);
                  }}
                  title={isRealtimeDrawing ? '关闭轨迹绘制' : '打开轨迹绘制'}
                />
              </div>
              {canFetch && (
                <button
                  type="button"
                  style={dropItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrackOpen(false);
                    onFetchTrack?.(member);
                  }}
                >
                  获取轨迹
                </button>
              )}
              {canRealtime && (
                <button
                  type="button"
                  style={dropItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrackOpen(false);
                    if (isRealtimeDrawing) onStopRealtimeTrack?.(member);
                    else onStartRealtimeTrack?.(member);
                  }}
                >
                  {isRealtimeDrawing ? '停止绘制' : '绘制轨迹'}
                </button>
              )}
              {canView && (
                <button
                  type="button"
                  style={dropItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrackOpen(false);
                    onToggleTrack?.(member);
                  }}
                >
                  {hasDeviceTrack ? '隐藏轨迹' : '查看轨迹'}
                </button>
              )}
              {canDownload && (
                <button
                  type="button"
                  style={dropItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrackOpen(false);
                    onDownloadTrack?.(member);
                  }}
                >
                  下载轨迹
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ position: 'relative' }}>
          <button
            type="button"
            style={{ ...actionBtn, padding: '0 8px', minWidth: 32, justifyContent: 'center' }}
            onClick={(e) => {
              e.stopPropagation();
              setTrackOpen(false);
              setMoreOpen((v) => !v);
            }}
            title="更多操作"
          >
            ···
          </button>
          {moreOpen && (
            <div style={{ ...dropMenu, left: 'auto', right: 0 }} role="menu">
              <button
                type="button"
                style={dropItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setMoreOpen(false);
                  onFlyTo?.(member);
                }}
              >
                定位到地图
              </button>
              <button
                type="button"
                style={dropItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setMoreOpen(false);
                  if (isStreaming) onOpenLiveStream?.(member);
                  else alert(`${member.name} 当前未在直播`);
                }}
              >
                {isStreaming ? '观看直播' : '直播（未开播）'}
              </button>
              <button
                type="button"
                style={dropItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setMoreOpen(false);
                  onToggleMapHidden?.(member);
                }}
              >
                {isHiddenOnMap ? '地图上显示' : '地图上隐藏'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function openCommandFallback(member) {
  try {
    // eslint-disable-next-line no-console
    console.info('[MemberTreeRow] 发指令', member?.name || member?.device_unique_id);
  } catch (_) { /* ignore */ }
}

/**
 * 队员树单行（含展开详情）
 * 默认只看状态；点开查看数据；低频操作收入「轨迹 / 更多」
 */
export default function MemberTreeRow({
  member,
  theme,
  openMember,
  highlightedMemberName,
  showTracks = {},
  isMemberTrackVisible,
  presenceById = null,
  onlineTimeoutMs,
  offlineTimeoutMs,
  nameAnchorRefs,
  isStreaming,
  isHiddenOnMap,
  isDrawing,
  toggleMemberExpand,
  openCommandMenuFor,
  flyToMember,
  setActiveMemberId,
  setHighlightedMemberName,
  toggleTrack,
  openLiveStream,
  onToggleMemberMapHidden,
  startRealTimeTrackDrawing,
  stopTrackFeatures,
  copyToClipboard,
  openDownloadTrackModal,
  fetchMemberTrack,
  onMemberIconChange,
  commandFeatures = {},
  notesByKey = null,
  missions = null,
  voicePulseIds = null,
  onVoiceClick = null
}) {
  const memberId = member.device_unique_id || member.name;
  const displayName = getMemberListDisplayName(member, notesByKey);
  const presence = (presenceById && memberId && presenceById[memberId])
    || getMemberPresenceStatus(
      member,
      Date.now(),
      onlineTimeoutMs,
      offlineTimeoutMs
    );
  const presenceLabelText = presence === PRESENCE_STATUS.ONLINE
    ? '在线'
    : (presence === PRESENCE_STATUS.TEMP_OFFLINE ? '暂时离线' : '离线');
  const memberKey = getMemberExpandKey(member);
  const isExpanded = !!openMember[memberKey];
  const isOffline = presence === PRESENCE_STATUS.OFFLINE || presence === 'offline';
  const batteryText = member.battery_level != null ? `电量 ${member.battery_level}%` : null;
  const seenText = formatMemberSeen(member.last_seen, isOffline);
  const accuracyText = null;
  const nameTooltip = [
    displayName,
    presenceLabelText,
    batteryText,
    seenText,
    member.org_name ? `组织 ${member.org_name}` : null,
    member.group_name ? `小组 ${member.group_name}` : null,
    member.device_unique_id ? `设备 ${member.device_unique_id}` : null
  ].filter(Boolean).join('\n');
  const t = theme;
  const streaming = isStreaming(member.name);
  const hidden = isHiddenOnMap(member);
  const trackVisible = isMemberTrackVisible
    ? isMemberTrackVisible(member.name)
    : !!showTracks[member.name];
  const drawing = typeof isDrawing === 'function' ? !!isDrawing(member.name) : false;
  const isHighlighted = highlightedMemberName === member.name;
  const missionTag = findMissionTag(missions, member);
  const voicePulsing = !!(voicePulseIds && (
    voicePulseIds.has?.(memberId)
    || voicePulseIds.has?.(member.name)
    || (Array.isArray(voicePulseIds) && (voicePulseIds.includes(memberId) || voicePulseIds.includes(member.name)))
  ));

  const handleTrackToggle = (next) => {
    if (next) {
      startRealTimeTrackDrawing?.(member.name, { auto: false });
    } else {
      stopTrackFeatures?.(member.name);
    }
  };

  return (
    <div key={member.device_unique_id || member.name} style={t.memberWrap}>
      <MemberCollapsedCard
        displayName={displayName}
        presenceStatus={presence}
        expanded={isExpanded}
        onToggleExpand={() => toggleMemberExpand(member, memberKey, isExpanded)}
        nameTitle={nameTooltip + '\n点击打开菜单（定位请用右侧定位钮）'}
        onNameClick={(e) => {
          const id = member.device_unique_id || member.name;
          setActiveMemberId?.(id);
          setHighlightedMemberName?.(member.name);
          openCommandMenuFor(member, e.currentTarget);
        }}
        nameRef={(el) => {
          const id = member.device_unique_id || member.name;
          if (!nameAnchorRefs?.current) return;
          if (el) nameAnchorRefs.current[id] = el;
          else delete nameAnchorRefs.current[id];
        }}
        showVoice
        voiceNode={(
          <VoiceIconButton
            unread={voicePulsing}
            onClick={() => {
              if (onVoiceClick) onVoiceClick(member);
              else openCommandMenuFor(member, nameAnchorRefs?.current?.[memberId]);
            }}
          />
        )}
        missionTag={missionTag}
        onLocate={() => {
          const id = member.device_unique_id || member.name;
          flyToMember?.(member);
          setActiveMemberId?.(id);
          setHighlightedMemberName?.(member.name);
        }}
        isStreaming={streaming}
        onOpenLive={() => openLiveStream?.(member)}
        freqBadge={<FreqStatusBadge member={member} presenceStatus={presence} />}
        statusText={seenText}
        batteryText={batteryText}
        accuracyText={accuracyText}
        trackOn={drawing}
        onTrackToggle={handleTrackToggle}
        trackTitle={drawing ? '关闭该队员轨迹绘制' : '打开该队员轨迹绘制（列表默认关闭）'}
        showCollapsedTrackSwitch={false}
        rowStyle={isHighlighted ? { background: '#fff4e5', borderColor: '#ffcc80' } : null}
      />
      {isExpanded && (
        <MemberDetail
          member={member}
          presenceStatus={presence}
          onFlyTo={flyToMember}
          onOpenCommandMenu={(m) => openCommandMenuFor(m, nameAnchorRefs?.current?.[m.device_unique_id || m.name])}
          onSendCommand={(m) => openCommandMenuFor(m, nameAnchorRefs?.current?.[m.device_unique_id || m.name])}
          onToggleTrack={() => toggleTrack?.(member.name)}
          onStartRealtimeTrack={() => startRealTimeTrackDrawing?.(member.name)}
          onStopRealtimeTrack={() => stopTrackFeatures?.(member.name)}
          isRealtimeDrawing={drawing}
          hasDeviceTrack={trackVisible}
          onCopyCoords={copyToClipboard}
          onDownloadTrack={openDownloadTrackModal}
          onFetchTrack={(m) => fetchMemberTrack?.(m)}
          onIconChange={onMemberIconChange}
          onOpenLiveStream={openLiveStream}
          isStreaming={streaming}
          isHiddenOnMap={hidden}
          onToggleMapHidden={onToggleMemberMapHidden}
          commandFeatures={commandFeatures}
        />
      )}
    </div>
  );
}
